// ─── Public Status Page — network layer ──────────────────────────────
//
// Every request here is deliberately DUMB and defensive. This page's entire
// reason to exist is to render when the rest of the platform does not, so:
//
//  * `credentials: 'omit'` on EVERY call. The status page may be served from the
//    same registrable domain as PCP itself, and a visitor may be carrying a
//    lapsed PCP session cookie. Sending it would make the API answer 401 — and
//    a 401 is exactly what the app's installAuthFetch() turns into a full-screen
//    lock. The public page never installs that bridge, but it must also never
//    hand the server a reason to challenge it: an anonymous request can only
//    come back as public data.
//  * A hard 4 s timeout on both hops. A hung TCP connection to a dying API is
//    the single most likely failure during a real incident, and a page that
//    spins forever is worse than one that says "here is the last known state".
//  * The static snapshot fallback. /snapshot/v3/*.json are plain files written
//    by the API ahead of time and served by the web container's own nginx — no
//    API process, no database, no connection pool. If the live call fails for
//    ANY reason (network, timeout, 5xx, HTML error page instead of JSON), we
//    read those instead and the page still renders.

import type {
  PublicStatusIncident,
  PublicStatusSummary,
  StatusChannel,
  StatusSubscribeBody,
  StatusSubscribeComponentOption,
  StatusSubscribeNext,
  StatusSubscribeSessionResponse,
} from '@pcp/shared';

/** Where the payload on screen actually came from. Drives the DegradedNotice. */
export type StatusSource = 'live' | 'snapshot';

export interface StatusFetchResult {
  data: PublicStatusSummary;
  source: StatusSource;
  /** epoch ms — when THIS client received it, not when the server generated it. */
  fetchedAt: number;
}

/** Both hops get their own budget, so a slow live call cannot eat the fallback's. */
const REQUEST_TIMEOUT_MS = 4000;

export class StatusApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(message: string, status = 0, code = 'request_failed') {
    super(message);
    this.name = 'StatusApiError';
    this.status = status;
    this.code = code;
  }
}

/** AbortSignal.timeout with a controller fallback for engines that lack it. */
function timeoutSignal(ms: number): { signal: AbortSignal; done: () => void } {
  const factory = (AbortSignal as unknown as { timeout?: (ms: number) => AbortSignal }).timeout;
  if (typeof factory === 'function') return { signal: factory.call(AbortSignal, ms), done: () => undefined };
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  return { signal: controller.signal, done: () => clearTimeout(timer) };
}

interface RequestOptions {
  method?: 'GET' | 'POST';
  body?: unknown;
  timeoutMs?: number;
}

async function request<T>(url: string, options: RequestOptions = {}): Promise<T> {
  const { signal, done } = timeoutSignal(options.timeoutMs ?? REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(url, {
      method: options.method ?? 'GET',
      // See the header comment — never send the visitor's PCP cookies.
      credentials: 'omit',
      headers: options.body === undefined
        ? { Accept: 'application/json' }
        : { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal,
    });
    if (!response.ok) {
      const detail = await readErrorDetail(response);
      throw new StatusApiError(detail.message, response.status, detail.code);
    }
    const text = await response.text();
    if (!text) throw new StatusApiError('Empty response', response.status, 'empty_response');
    try {
      return JSON.parse(text) as T;
    } catch {
      // A proxy or an error page answering 200 with HTML — treat as a failure so
      // the snapshot fallback engages.
      throw new StatusApiError('Response was not JSON', response.status, 'invalid_json');
    }
  } finally {
    done();
  }
}

async function readErrorDetail(response: Response): Promise<{ message: string; code: string }> {
  try {
    const text = await response.text();
    if (text) {
      const body = JSON.parse(text) as { error?: string; code?: string; message?: string };
      const code = body.code ?? body.error ?? 'request_failed';
      return { message: body.message ?? body.error ?? `HTTP ${response.status}`, code };
    }
  } catch {
    /* fall through to the status line */
  }
  return { message: `HTTP ${response.status}`, code: 'request_failed' };
}

/**
 * The API wraps successful payloads as `{ data: … }`, while the static snapshot
 * files ARE the payload. Accept both rather than coupling this page to one of
 * them: `v` is the discriminator (PublicStatusSummary always carries `v: 1`).
 */
function unwrap<T>(body: unknown, marker: string): T | null {
  if (!body || typeof body !== 'object') return null;
  const root = body as Record<string, unknown>;
  if (marker in root) return root as unknown as T;
  const nested = root.data;
  if (nested && typeof nested === 'object' && marker in (nested as Record<string, unknown>)) {
    return nested as T;
  }
  return null;
}

/** Guarantee the arrays exist so no consumer needs a `?? []` on every access. */
function normalizeSummary(summary: PublicStatusSummary): PublicStatusSummary {
  return {
    ...summary,
    audiences: Array.isArray(summary.audiences) ? summary.audiences : [],
    components: Array.isArray(summary.components) ? summary.components : [],
    incidents: Array.isArray(summary.incidents) ? summary.incidents : [],
    historyDays: Number.isFinite(summary.historyDays) ? summary.historyDays : 0,
  };
}

function summaryUrls(audience: string | null): { live: string; snapshot: string } {
  const slug = audience ? encodeURIComponent(audience) : null;
  return {
    live: slug ? `/api/status/v1/summary?audience=${slug}` : '/api/status/v1/summary',
    // v3 = the nested component tree plus the minute-accurate downtime series.
    // This directory name is the SCHEMA_DIR constant in
    // services/status/snapshot.ts — the two move together, or the page reads a
    // generation written to a shape it no longer understands.
    snapshot: slug ? `/snapshot/v3/summary-${slug}.json` : '/snapshot/v3/summary.json',
  };
}

/**
 * LIVE first, static snapshot second. Only a total failure of BOTH throws — and
 * even then the caller keeps the last good render on screen.
 */
export async function fetchStatus(audience: string | null): Promise<StatusFetchResult> {
  const urls = summaryUrls(audience);
  try {
    const live = unwrap<PublicStatusSummary>(await request<unknown>(urls.live), 'v');
    if (live) return { data: normalizeSummary(live), source: 'live', fetchedAt: Date.now() };
  } catch {
    /* fall through — any live failure is a snapshot read, never an error yet */
  }
  const snapshot = unwrap<PublicStatusSummary>(await request<unknown>(urls.snapshot), 'v');
  if (!snapshot) throw new StatusApiError('Snapshot payload was unreadable', 0, 'snapshot_unreadable');
  return { data: normalizeSummary(snapshot), source: 'snapshot', fetchedAt: Date.now() };
}

/**
 * A COLD deep link: /incident/<ref> for an incident older than the summary
 * window (or one that belongs to a different audience). Returns null for "not
 * found" AND for "we could not find out" — the detail view renders the same
 * Danish "we couldn't find it" card either way, and a stack trace on a public
 * page helps nobody.
 */
export async function fetchIncident(ref: string, audience: string | null): Promise<PublicStatusIncident | null> {
  const slug = audience ? `?audience=${encodeURIComponent(audience)}` : '';
  try {
    const body = await request<unknown>(`/api/status/v1/incidents/${encodeURIComponent(ref)}${slug}`);
    return unwrap<PublicStatusIncident>(body, 'refId');
  } catch {
    return null;
  }
}

// ─── Subscribe ───────────────────────────────────────────────────────

/**
 * One short-lived session per opened subscribe form. It carries the anti-bot
 * form token, the Turnstile site key (empty ⇒ no captcha configured), and the
 * audiences WITH their ids — the subscribe call keys on audience ids, while the
 * public summary only ever exposes slugs.
 */
/** One node of the component tree, flattened. `parentId` is what the form
 *  rebuilds the tree from — the sheet renders it as a collapsible checkbox
 *  tree, so it needs the edges, not just the depth. */
// The subscribe/manage shapes are NO LONGER declared here. They live in
// @pcp/shared (status-public-api.ts) and are imported by BOTH this client and
// the Fastify route, so a field rename is a compile error rather than a value
// that silently reads as undefined on one side. Five shipped bugs came from
// maintaining these twice — see that file's header.
export type StatusSubscribeComponent = StatusSubscribeComponentOption;
export type StatusSubscribeSession = StatusSubscribeSessionResponse & {
  /** @deprecated Wire alias the server may still send; normalised away by
   *  postSubscribeSession so no caller has to know it exists. */
  minFormDwellMs?: number;
};

export async function postSubscribeSession(): Promise<StatusSubscribeSession> {
  const body = await request<unknown>('/api/status/v1/subscribe/session', { method: 'POST', body: {} });
  const session = unwrap<StatusSubscribeSession>(body, 'formToken');
  if (!session) throw new StatusApiError('Subscribe session was unreadable', 0, 'session_unreadable');
  return {
    ...session,
    turnstileSiteKey: session.turnstileSiteKey ?? '',
    audiences: Array.isArray(session.audiences) ? session.audiences : [],
    components: Array.isArray(session.components) ? session.components : [],
    // Normalise the dwell floor to ONE name here, so no caller has to know the
    // alias exists.
    minDwellMs: session.minDwellMs ?? session.minFormDwellMs ?? 0,
  };
}

export type StatusSubscribeRequest = StatusSubscribeBody;

/** Re-exported from the shared contract — declared ONCE, in @pcp/shared. The
 *  client used to carry its own copy that also listed 'otp', a value the server
 *  never sends. */
export type { StatusSubscribeNext } from '@pcp/shared';

export interface StatusSubscribeResult {
  ok: boolean;
  /** The first requested channel — kept for older callers. */
  channel: StatusChannel;
  next: StatusSubscribeNext;
  /** One entry per channel this submit actually asked for. */
  results: Array<{ channel: StatusChannel; next: StatusSubscribeNext }>;
}

export async function postSubscribe(input: StatusSubscribeRequest): Promise<StatusSubscribeResult> {
  const body = await request<unknown>('/api/status/v1/subscribe', { method: 'POST', body: input });
  const result = unwrap<StatusSubscribeResult>(body, 'ok');
  if (!result) throw new StatusApiError('Subscribe response was unreadable', 0, 'subscribe_unreadable');
  const fallback: StatusChannel = input.email ? 'email' : 'sms';
  return {
    ok: Boolean(result.ok),
    channel: result.channel ?? fallback,
    next: result.next ?? 'none',
    // An older API answers without `results`; synthesise the single-channel
    // shape so every caller can read one code path.
    results: Array.isArray(result.results)
      ? result.results
      : [{ channel: result.channel ?? fallback, next: result.next ?? 'none' }],
  };
}

export interface StatusVerifyRequest {
  channel: StatusChannel;
  destination: string;
  /** The 6-digit code from the SMS. */
  code: string;
  formToken: string;
}

export interface StatusVerifyResult {
  ok: boolean;
  /** Why it failed, so the sheet can pick the right Danish message. */
  reason?: 'invalid' | 'expired' | 'locked' | 'unknown';
}

/** Second leg of the SMS flow: exchange the one-time code for a confirmation. */
export async function postSubscribeVerify(input: StatusVerifyRequest): Promise<StatusVerifyResult> {
  const body = await request<unknown>('/api/status/v1/subscribe/verify', { method: 'POST', body: input });
  const result = unwrap<StatusVerifyResult>(body, 'ok');
  if (!result) throw new StatusApiError('Verify response was unreadable', 0, 'verify_unreadable');
  return { ok: Boolean(result.ok), reason: result.reason };
}

/** Ask for a fresh one-time code when the first SMS never arrived / expired. */
export async function postSubscribeResend(input: { channel: StatusChannel; destination: string; formToken: string }): Promise<{ ok: boolean }> {
  const body = await request<unknown>('/api/status/v1/subscribe/resend', { method: 'POST', body: input });
  const result = unwrap<{ ok: boolean }>(body, 'ok');
  return { ok: Boolean(result?.ok) };
}

// ─── A subscriber's own subscription ─────────────────────────────────
//
// Reached from a link in an email or an SMS. The SHORT CODE in that link is the
// only credential — see the header of services/status/subscribers.ts for why an
// email never embeds the manage key itself, and why resolving a short code MINTS
// one. So these calls send whichever the page was given and the server resolves
// it; the page never has to know the difference.

export interface StatusSubscriptionView {
  /** The subscriber row, WITHOUT the destination — a manage link can be
   *  forwarded, and the holder does not need the full address to tick boxes. */
  subscriber: {
    id: string;
    channel: StatusChannel;
    status: 'pending' | 'confirmed' | 'unsubscribed' | 'bounced';
    /** Audience SLUGS. The field is `audiences` because that is what the server
     *  DTO calls it — checked against toDto(), not assumed. Naming it anything
     *  else here silently resolves to undefined, which is exactly how b-01547
     *  and b-01550 happened. */
    audiences?: string[];
    /** Component IDS (components have no slug). EMPTY = every system, the same
     *  "no filter" rule as audiences — NOT "nothing". */
    componentIds?: string[];
    /** Safe to show: the server strips the raw destination and keeps only this. */
    destinationMasked?: string;
    createdAt?: string;
    confirmedAt?: string | null;
  };
  /** The manage key the server resolved, for the follow-up writes. */
  key: string;
  audiences: Array<{ id: string; slug: string; name: string }>;
  /** The systems picker's OPTIONS — the same flattened tree the subscribe form
   *  gets, from the same server helper, so the two pickers cannot drift. */
  components: StatusSubscribeComponent[];
}

/** Everything the page needs, or null when the link does not resolve. Null for
 *  "expired" AND for "we could not find out": the page says the same thing
 *  either way, and distinguishing them would let anyone probe codes. */
export async function fetchSubscription(input: { code?: string | null; key?: string | null }): Promise<StatusSubscriptionView | null> {
  const params = new URLSearchParams();
  if (input.key) params.set('key', input.key);
  else if (input.code) params.set('code', input.code);
  if (![...params.keys()].length) return null;
  try {
    const body = await request<unknown>(`/api/status/v1/manage?${params.toString()}`);
    return unwrap<StatusSubscriptionView>(body, 'subscriber');
  } catch {
    return null;
  }
}

/** Save a changed audience selection. Empty `audienceIds` means ALL — the same
 *  rule the subscribe form states. */
export async function postSubscriptionPreferences(input: {
  code?: string | null;
  key?: string | null;
  audienceIds: string[];
  /** Component ids. EMPTY is a real instruction — "clear my filter, send me
   *  everything" — so it is always sent, never omitted when empty. */
  componentIds: string[];
  /** 'confirmed' RE-SUBSCRIBES a row that was unsubscribed — the same write,
   *  so the row keeps its history instead of starting over. Omit to leave the
   *  status alone; the server ignores anything other than the two it accepts. */
  status?: 'confirmed' | 'unsubscribed';
}): Promise<boolean> {
  try {
    await request<unknown>('/api/status/v1/manage', {
      method: 'POST',
      body: {
        ...(input.key ? { key: input.key } : {}),
        ...(input.code ? { code: input.code } : {}),
        audienceIds: input.audienceIds,
        // Sent unconditionally, INCLUDING when empty: the server reads absent as
        // "leave the systems alone" and [] as "clear the filter", so spreading
        // this conditionally would make clearing a selection impossible.
        componentIds: input.componentIds,
        ...(input.status ? { status: input.status } : {}),
      },
    });
    return true;
  } catch {
    return false;
  }
}

/**
 * Stop everything. The server ALWAYS reports success, including for a code that
 * matches nothing — a truthful "no such subscriber" would let anyone test codes
 * for existence, and "you will not receive these any more" is true either way.
 * Reversible from this same page, which is what makes the one-click form safe.
 */
export async function postUnsubscribe(input: { code?: string | null; key?: string | null }): Promise<boolean> {
  try {
    await request<unknown>('/api/status/v1/unsubscribe', {
      method: 'POST',
      body: { ...(input.key ? { key: input.key } : {}), ...(input.code ? { code: input.code } : {}) },
    });
    return true;
  } catch {
    return false;
  }
}
